import { useEffect, useState } from 'react';
import type { OrderDetail } from '@/api/types';
import { api, ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { qty } from '@/lib/format';
import { chooseOrderAssembly } from './api';
import s from './OrderAssemblyDialog.module.css';

type AssemblyLine = { id: string; name: string; workers?: { id: string; name: string }[]; isActive?: boolean };
type Mode = 'REQUIRED' | 'NOT_REQUIRED';
const READONLY = import.meta.env.VITE_READONLY !== '0';

export function OrderAssemblyDialog({ order, canManage, onClose, onSuccess }: { order: OrderDetail; canManage: boolean; onClose: () => void; onSuccess: () => void }) {
  const bundleKeys = new Set(order.lines.map((line) => line.bundleInstanceId || line.bundleId).filter(Boolean));
  const [mode, setMode] = useState<Mode>(bundleKeys.size ? 'REQUIRED' : 'NOT_REQUIRED');
  const [lines, setLines] = useState<AssemblyLine[]>([]);
  const [lineId, setLineId] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const close = () => { if (done) onSuccess(); onClose(); };
  const line = lines.find((item) => item.id === lineId);
  const allowedStatus = ['IN_PREPARATION', 'STOCK_READY', 'PARTIALLY_SHIPPED'].includes(order.status);
  const stockAllowed = !!(order.stockStatus?.canStartAssembly || order.stockStatus?.canShip);

  useEffect(() => {
    const controller = new AbortController();
    void api.get<AssemblyLine[]>('/api/assembly-lines', undefined, controller.signal)
      .then((result) => { setLines(result || []); setLoading(false); })
      .catch((cause: Error) => { if (cause.name !== 'AbortError') { setListError(cause.message); setLoading(false); } });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onSuccess, done, saving]);

  const submit = async () => {
    if (READONLY || !canManage || !allowedStatus || !stockAllowed || mode === 'REQUIRED' && !line || saving || uncertain) return;
    setSaving(true);
    setError('');
    try { await chooseOrderAssembly(order.id, mode, lineId); setDone(true); }
    catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '组装决策失败');
    } finally { setSaving(false); }
  };

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label="确认组装方式">
      <div className={s.grab} aria-hidden="true" />
      <header className={s.head}><button type="button" aria-label="关闭" disabled={saving} onClick={close}>×</button><div className={s.title}><b>确认组装方式</b><small>{order.orderNo}</small></div><span /></header>
      {done ? <div className={s.done}><Icon name="check" size={24} /><b>{mode === 'REQUIRED' ? '已开始组装' : '已进入待发阶段'}</b><p>V1 共用订单状态与组装线快照已更新。</p><button type="button" onClick={close}>完成</button></div> : <>
        <div className={s.body}>
          <p className={s.hint}>这张订单含 {bundleKeys.size} 个套装{bundleKeys.size ? '，默认选择“需要组装”' : '，可直接进入待发阶段'}。</p>
          <div className={s.options} role="group" aria-label="组装方式"><button type="button" aria-pressed={mode === 'REQUIRED'} onClick={() => setMode('REQUIRED')}>需要组装<small>进组装线排产</small></button><button type="button" aria-pressed={mode === 'NOT_REQUIRED'} onClick={() => setMode('NOT_REQUIRED')}>无需组装<small>直接进入待发</small></button></div>
          {mode === 'REQUIRED' && <div className={s.group}><label><span>* 组装线</span><select value={lineId} onChange={(event) => setLineId(event.target.value)}><option value="">选择组装线</option>{lines.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><div className={s.workers}><span>工人</span><b>{line?.workers?.map((worker) => worker.name).join('、') || '选择组装线后显示'}</b></div></div>}
          {loading && mode === 'REQUIRED' && <p className={s.hint}>正在读取组装线…</p>}
          {listError && mode === 'REQUIRED' && <p className={s.error}>组装线读取失败：{listError}</p>}
          <div className={s.notice}><Icon name="alert" size={18} /><div><strong>{order.stockStatus?.shortageQuantity ? `当前缺货 ${qty(order.stockStatus.shortageQuantity)} 个` : '库存将按当前订单核对'}</strong><p>{order.stockStatus?.shortageRemark || '只要当前有可安排的实物，就可以先组装可配齐的部分。'}</p></div></div>
          {mode === 'REQUIRED' && <p className={s.hint}>组装线名称和工人名单会存入订单快照，之后改名不影响这张订单。</p>}
          {!stockAllowed && <p className={s.error}>当前没有可安排组装或待发的库存，V1 会拒绝此操作。</p>}
          {READONLY && <p className={s.hint}>当前为生产只读模式，可查看窗口，暂不能提交。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对订单状态，暂勿重复提交。'}</p>}
        </div>
        <footer className={s.footer}><button type="button" onClick={close} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={READONLY || !canManage || !allowedStatus || !stockAllowed || mode === 'REQUIRED' && !line || saving || uncertain} onClick={() => void submit()}>{saving ? '处理中…' : mode === 'REQUIRED' ? '开始组装' : '进入待发'}</button></footer>
      </>}
    </section>
  </div>;
}
